import { context, trace } from '@opentelemetry/api';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { isNil, noop } from 'lodash';
import { useAccount } from '../account-configurations';
import { APP_EVENT_TYPE } from '../app-events-aggregator';
import { useAppEventExtension } from '../app-events-aggregator/hooks/useAppEventExtension';
import { authentificationSelector, userSelector } from '../authentication';
import { throwCriticalError } from '../formData/utils/throwCriticalError';
import { useIsTracerPermitted } from './hooks/useIsTracerPermitted';
import { startTracing, TRACER_NAME } from './utils/tracerConfiguration';

export const SpanAlreadyExistsError = new Error('Span with the same name already exists.');
const EMPTY_MAP = new Map();
const CHILD_EMPTY_MAP = new Map();
export const TracerContext = createContext({
    createRootSpan: noop,
    getRootSpan: noop,
    endRootSpan: noop,
    startSpan: noop,
    getSpan: noop,
    endSpan: noop,
    setSpanStatus: noop,
});

export const useTracerContext = () => useContext(TracerContext);

export const TracerProvider = ({ children }) => {
    const isPermitted = useIsTracerPermitted();
    const isAuthenticated = useSelector(authentificationSelector);
    const { accountName } = useAccount();
    const { user_id } = useSelector(userSelector);
    const extendEvent = useAppEventExtension();

    const tracer = trace.getTracer(TRACER_NAME);
    const globalSpanRef = useRef(EMPTY_MAP);
    const childSpansRef = useRef(CHILD_EMPTY_MAP);

    const getRootSpan = useCallback(
        name => {
            if (!isNil(name)) {
                return globalSpanRef.current.get(name);
            }
        },
        [globalSpanRef]
    );

    const setEventAttributes = useCallback(
        async span => {
            const fullEvent = await extendEvent({ type: APP_EVENT_TYPE.PERFORMANCE_MEASUREMENT });
            span.setAttributes(fullEvent);
        },
        [extendEvent]
    );

    const createRootSpan = useCallback(
        (name, options) => {
            if (globalSpanRef.current.has(name)) {
                throwCriticalError(SpanAlreadyExistsError);
            }

            const span = tracer.startSpan(name, options);
            span.setAttributes({ UserId: user_id });

            globalSpanRef.current.set(name, span);

            setEventAttributes(span).catch(error => {
                console.error('Failed to add events to span:', error);
            });

            return span;
        },
        [tracer, user_id, setEventAttributes]
    );

    const startSpan = useCallback(
        (name, parentSpan, attributes = {}) => {
            const ctxWithParent = trace.setSpan(context.active(), parentSpan);

            context.with(ctxWithParent, () => {
                const childSpan = tracer.startSpan(name);
                childSpan.setAttributes(attributes);

                childSpansRef.current.set(name, childSpan);
            });
        },
        [tracer]
    );

    const getSpan = useCallback(
        name => {
            if (!isNil(name)) {
                return childSpansRef.current.get(name);
            }
        },
        [childSpansRef]
    );

    const endSpan = useCallback(name => {
        const span = childSpansRef.current.get(name);
        if (span) {
            span.end();
            childSpansRef.current.delete(name);
        }
    }, []);

    const endRootSpan = useCallback(
        name => {
            const span = globalSpanRef.current.get(name);
            if (span) {
                span.end();
                globalSpanRef.current.delete(name);
            }
        },
        [globalSpanRef]
    );

    const setSpanStatus = useCallback((span, status, message) => {
        if (span) {
            span.setStatus({ code: status, message });
        }
    }, []);

    useEffect(() => {
        globalSpanRef.current.clear();
        childSpansRef.current.clear();
        if (isAuthenticated) {
            startTracing();
        }
    }, [isAuthenticated]);

    useEffect(() => {
        globalSpanRef.current?.forEach((_, name) => {
            endRootSpan(name);
        });
    }, [accountName, endRootSpan]);

    const value = useMemo(
        () => ({
            createRootSpan,
            getRootSpan,
            endRootSpan,
            startSpan,
            getSpan,
            endSpan,
            childSpansRef,
            setSpanStatus,
        }),
        [createRootSpan, getRootSpan, endRootSpan, startSpan, getSpan, endSpan, setSpanStatus]
    );

    if (!isPermitted) {
        return <TracerContext.Provider value={TracerContext._currentValue}>{children}</TracerContext.Provider>;
    }

    return <TracerContext.Provider value={value}>{children}</TracerContext.Provider>;
};
